import { InfoIcon } from '@chakra-ui/icons';
import {
  Card,
  CardBody,
  Flex,
  HStack,
  Heading,
  Hide,
  Icon,
  Skeleton,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { routes } from '@blockpulse3/data/shared';
import { FundraisingStatus, useGetSubscriptionQuery } from '@blockpulse3/graphql/hooks';
import { formatDate } from '@blockpulse3/helpers';
import { CompanyIdentityAvatar, ErrorQueryCard, useErrorToast } from '@blockpulse3/ui/commons';

type Props = unknown;

/**
 * SubscriptionHeader.
 *
 * @returns {JSX.Element}
 */
export function SubscriptionHeader(): JSX.Element {
  const i18nSubscription = useTranslations('Subscription');
  const i18nSubscriptionHeader = useTranslations('SubscriptionHeader');

  const navigate = useNavigate();
  const { subscriptionId = '', companyId } = useParams();

  const errorToast = useErrorToast();

  const { data, loading, error } = useGetSubscriptionQuery({
    variables: { subscriptionId },
    onError: () => {
      if (companyId) {
        navigate(routes.workspace.fmt(companyId));
      } else {
        navigate(routes.me.href);
      }
      errorToast({ title: i18nSubscription('Toast.Error') });
    },
  });

  if (loading) {
    return (
      <Skeleton>
        <Card h="200px" />
      </Skeleton>
    );
  }

  if (error) {
    return <ErrorQueryCard />;
  }

  if (!data) {
    return <ErrorQueryCard />;
  }

  const { subscription } = data;

  const endDate = subscription.fundraising.endSubscriptionDate;
  const isClosed = [
    FundraisingStatus.CLOSED,
    FundraisingStatus.REVIEWED,
    FundraisingStatus.FINALIZED,
  ].includes(subscription.fundraising.status);

  return (
    <Card>
      <Flex as={CardBody} direction={{ base: 'column', md: 'row' }}>
        <Hide below="md">
          <CompanyIdentityAvatar
            boxSize="40"
            src={subscription.fundraising.company.identity?.profilePicture}
          />
        </Hide>
        <Stack
          alignItems="flex-start"
          justifyContent="center"
          pb={{ base: '4', md: '0' }}
          px={{ base: '0', md: '6' }}
          width="full"
        >
          <Text color="gray.500" fontSize="xs" fontWeight="semibold">
            {i18nSubscriptionHeader('Title')}
          </Text>
          <Heading fontWeight="bold">{subscription?.fundraising.name}</Heading>
        </Stack>
        <Flex
          alignItems="flex-end"
          bg="gray.50"
          direction="column"
          flexShrink="0"
          justifyContent="center"
          p="5"
          rounded="sm"
        >
          <HStack>
            <Text color="gray.600" fontWeight="500">
              {i18nSubscriptionHeader('EndIn')}
            </Text>
            {!isClosed && (
              <Tooltip
                hasArrow
                placement="top"
                label={i18nSubscriptionHeader('Tooltip.Started', {
                  date: formatDate(endDate, 'DD MMM YYYY à HH:mm z'),
                })}
              >
                <Icon as={InfoIcon} color="gray.600" />
              </Tooltip>
            )}
          </HStack>
          <Text color="gray.600" fontSize="2xl" fontWeight="600">
            {formatDate(endDate, 'D MMMM YYYY')}
          </Text>
          {!isClosed && (
            <Text color="gray.600" fontSize="sm" fontWeight="600">
              {i18nSubscriptionHeader('RemainingDays', {
                days: subscription?.fundraising?.remainingDays,
              })}
            </Text>
          )}
        </Flex>
      </Flex>
    </Card>
  );
}

export type SubscriptionHeaderProps = Props;
